import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

import Icon from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WishListIcon from "@tCentro/Icons/WishListIcon";
import { css } from "styled-components";
import NISAddToWishListIcon from "@tCentro/Icons/NISAddToWishListIcon";
import { ProductAddToListLinkStyles } from "@tCentro/Overrides/Components/ProductAddToListLink";
import Button from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface NISProductAddToListLinkStyles {
    link?: LinkPresentationProps;
    container?: InjectableCss;
    wishListIcon?: InjectableCss;
    wishListText?: InjectableCss;
    addlistIconWrapper?: InjectableCss;
    titleTypography?: TypographyPresentationProps;
    addlistVerticalIconWrapper?: InjectableCss;
}

export const nisProductAddToListLinkStyles: NISProductAddToListLinkStyles = {
    addlistVerticalIconWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            background: none !important;
            border: none !important;
            gap: 8px;
            min-height: 0px;
            border-radius: 40px;
            padding: 0px;
            &:focus {
                outline-style: none !important;
            }
            &:hover {
                border: none !important;
                border-color: unset !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 4px;
                    `,
                    css`
                        gap: 4px;
                    `,
                    css`
                        gap: 4px;
                    `,
                    css``,
                    css``,
                ]);
            }}
            @media (min-width:991px) and (max-width:1024px) {
                gap: 4px !important;
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 0;
            margin-top: 0;
            color: ${getColor("primary")};
            text-transform: capitalize;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            font-family: Roboto;
            @media (min-width: 1080px) and (max-width: 1340px) {
                font-size: 12px;
                font-weight: 700;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        // display: none;
                    `,
                    css`
                        // display: none;
                    `,
                    css`
                        // display: none;
                    `,
                    css`
                        // @media (min-width: 993px) and (max-width: 1079px) {
                        //     display: none;
                        // }
                    `,
                ]);
            }}
        `,
        // ellipsis: true,
        // transform: "uppercase",
    },
    addlistIconWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            // margin: 0px 8px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    link: {
        css: css``,
    },
    wishListText: {
        css: css`
            font-family: Roboto;
            font-weight: 600;
            font-style: SemiBold;
            font-size: 16px;
            leading-trim: NONE;
            line-height: 16px;
            letter-spacing: 0%;
            text-align: center;
            vertical-align: middle;
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    wishListIcon: {
        css: css`
            width: 20px;
            height: 20px;
            span {
                width: 20px;
                height: 20px;
            }
            svg {
                width: 20px;
                height: 20px;
            }
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const nisProductAddToListLink = nisProductAddToListLinkStyles;
// const styles = nisProductAddToListLink;
const NISProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);

    const [styles] = React.useState(() => mergeToNew(nisProductAddToListLink, extendedStyles));
    if (!product.canAddToWishlist) {
        return null;
    }

    const addToListLinkClickHandler = () => {
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    return (
        <StyledWrapper {...styles.container}>
            {/* <Link
                {...styles.link}
                onClick={addToListLinkClickHandler}
                disabled={productInfo.qtyOrdered <= 0}
                {...otherProps}
            >
                <Icon {...styles.wishListIcon} src={NISAddToWishListIcon} />
                <StyledWrapper  {...styles.wishListText} as="span">{labelOverride ?? translate("Add to List ")}</StyledWrapper>
            </Link> */}
            <StyledWrapper {...styles.addlistIconWrapper}>
                <Button onClick={addToListLinkClickHandler} {...styles.addlistVerticalIconWrapper}>
                    <Icon {...styles.wishListIcon} src={NISAddToWishListIcon} />
                    <Typography {...styles.titleTypography}>{siteMessage("Add to List")}</Typography>
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(NISProductAddToListLink));
